import React, { lazy, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@linaria/react';
import { BottomCTA } from 'components/Accommodation/Offers/BottomCTA';
import { setScrollToApTarget } from 'modules/accommodation/slice';
import { AccommodationPageNavSections } from 'modules/accommodation/types';
import { getHotelDescriptionOverlay } from 'modules/overlay/selectors';
import { hideOverlay, showOverlay } from 'modules/overlay/slice';
import { OverlayType } from 'modules/overlay/types';
import { BottomSheet } from '@daedalus/atlas/BottomSheet';
import { Button } from '@daedalus/atlas/Button';
import { ButtonLink } from '@daedalus/atlas/ButtonLink';
import { useDeviceLayout } from '@daedalus/atlas/context/deviceLayout';
import { Divider } from '@daedalus/atlas/Divider';
import ContentWrapper from '@daedalus/atlas/helpers/ContentWrapper';
import { Flex } from '@daedalus/atlas/helpers/Flex';
import { PopUp } from '@daedalus/atlas/PopUp';
import { Text } from '@daedalus/atlas/Text';
import { useInView } from '@daedalus/core/src/_web/utils/browser/hooks/useInView';
import { useDispatchTeamEvent } from '@daedalus/core/src/analytics/hooks/useDispatchTeamEvent';
import { trackEvent } from '@daedalus/core/src/analytics/modules/actions';
import { Action, AnalyticsContext, Category, Entity, HotelContext } from '@daedalus/core/src/analytics/types/Events';
import FormattedMessageWrapper from '@daedalus/core/src/localization/components/FormattedMessage';
import { ContentHotel } from '@findhotel/sapi/dist/types/packages/core/src';
interface Props {
  hotel?: ContentHotel;
}
const HotelDescriptionsContent = lazy(() => import( /* webpackChunkName: "HotelDescriptionsContent" */
'./HotelDescriptionsContent'));
const _exp = /*#__PURE__*/() => Text;
export const StyledText = /*#__PURE__*/styled(_exp())({
  name: "StyledText",
  class: "s1pijwj3",
  propsAsIs: true
});

// Added as part of sapi4eva-hotel-descriptions
export const HotelDescriptions = ({
  hotel
}: Props) => {
  const {
    ref,
    inView
  } = useInView({
    triggerOnce: true,
    threshold: 0.5
  });
  const dispatchTeamEvent = useDispatchTeamEvent();
  const {
    isMobile
  } = useDeviceLayout();
  const {
    objectID: hotelId,
    descriptions = {}
  } = hotel || {};
  const {
    overall = '',
    version
  } = descriptions;
  const dispatch = useDispatch();
  const isOpen = Boolean(useSelector(getHotelDescriptionOverlay));
  const onClose = () => dispatch(hideOverlay());
  useEffect(() => {
    if (inView) {
      const hotelContext = {
        hotelId,
        isAIDescriptionEligible: version === 2
      } as HotelContext;
      dispatchTeamEvent(trackEvent({
        category: Category.User,
        entity: Entity.HotelDetailsDescription,
        action: Action.Viewed,
        analyticsContext: {
          [AnalyticsContext.HotelContext]: hotelContext
        }
      }));
    }
  }, [dispatchTeamEvent, hotelId, inView, version]);
  const handleShowMoreClick = () => {
    dispatchTeamEvent(trackEvent({
      category: Category.User,
      entity: Entity.HotelDetailsDescription,
      action: Action.Clicked
    }));
    dispatch(showOverlay({
      type: OverlayType.HotelDescription
    }));
  };
  const handleDesktopBottomCTAClick = () => {
    dispatch(hideOverlay());
    dispatch(setScrollToApTarget({
      target: AccommodationPageNavSections.Rooms
    }));
  };
  if (!Object.keys(descriptions || {}).length || !overall) return null;
  return <div ref={ref}>
      <ContentWrapper paddingX={isMobile ? 's500' : 's000'} paddingY={isMobile ? 's700' : 's000'}>
        <ContentWrapper paddingBottom="s500">
          <Text variant="titleL">
            <FormattedMessageWrapper id="propertyDescription" defaultMessage="Property description" />
          </Text>
        </ContentWrapper>
        <ContentWrapper paddingBottom="s500">
          <StyledText variant="bodyM">
            <Flex.Column gap="s400" dangerouslySetInnerHTML={{
            __html: `${overall.slice(0, 240)}...`
          }} />
          </StyledText>
        </ContentWrapper>
        <ButtonLink variant="default" size={isMobile ? 'md' : 'lg'} isCompact onClick={handleShowMoreClick}>
          <FormattedMessageWrapper id="showMore" defaultMessage="Show more" />
        </ButtonLink>

        {hotelId && isMobile && <BottomSheet titleBarProps={{
        centerContent: <FormattedMessageWrapper id="propertyDescription" defaultMessage="Property description" />
      }} withDragHandler footerContent={<BottomCTA hotelId={hotelId} sourceComponent="MobileHotelViewAllPoliciesStaticOfferCTA" />} isOpen={isOpen} onClose={onClose} prefersReducedMotion>
            <HotelDescriptionsContent overall={overall} />
          </BottomSheet>}

        {hotelId && !isMobile && <PopUp width={640} titleBarProps={{
        centerContent: <FormattedMessageWrapper id="propertyDescription" defaultMessage="Property description" />
      }} footer={<Button size="lg" fullWidth variant="primary" onClick={handleDesktopBottomCTAClick}>
                <FormattedMessageWrapper id="viewRooms" defaultMessage="View rooms" />
              </Button>} isOpen={isOpen} closeAction={onClose}>
            <HotelDescriptionsContent overall={overall} />
          </PopUp>}
      </ContentWrapper>
      {isMobile && <Divider sideMargin="s500" />}
    </div>;
};

require("./HotelDescriptions.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./HotelDescriptions.tsx");